import React, { useState, useEffect } from "react";
import { useParams, useHistory } from "react-router-dom";
import {
  Card,
  Button,
  Tabs,
  Form,
  Input,
  Select,
  Alert,
  Spin,
  Typography,
  Divider,
  Row,
  Col,
  message,
} from "antd";
import {
  ShoppingOutlined,
  DownOutlined,
  UpOutlined,
  CheckCircleOutlined,
  CopyOutlined,
  ClockCircleOutlined,
  GiftOutlined,
  SafetyCertificateOutlined,
  LockOutlined,
  ArrowRightOutlined,
  InfoCircleOutlined,
  ThunderboltOutlined,
  PhoneOutlined,
  WifiOutlined,
  DesktopOutlined,
} from "@ant-design/icons";
import axios from "axios";
import moment from "moment";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import "./style.css";

const { Title, Text, Paragraph } = Typography;
const { TabPane } = Tabs;
const { Option } = Select;

const API_BASE =
  process.env.REACT_APP_BASE_URL ||
  process.env.REACT_APP_LOYSTAR_BASE_URL ||
  "http://localhost:3000";

const ClaimRewardPage = () => {
  const { claim_token } = useParams();
  const history = useHistory();

  const [loading, setLoading] = useState(true);
  const [claimType, setClaimType] = useState(null); // 'loyalty_receipt' | 'utility_reward'
  const [error, setError] = useState(null);

  // --- Loyalty Receipt State ---
  const [receiptData, setReceiptData] = useState(null);
  const [loyaltyPhone, setLoyaltyPhone] = useState("");
  const [claimingLoyalty, setClaimingLoyalty] = useState(false);
  const [loyaltyClaimSuccess, setLoyaltyClaimSuccess] = useState(null);
  const [summaryOpen, setSummaryOpen] = useState(false);
  const [loyaltyForm] = Form.useForm();

  // --- Utility Reward State ---
  const [claimData, setClaimData] = useState(null);
  const [catalog, setCatalog] = useState({
    airtime_networks: [],
    electricity_billers: [],
    cable_billers: [],
  });
  const [activeTab, setActiveTab] = useState("electricity");
  const [submitting, setSubmitting] = useState(false);
  const [claimSuccess, setClaimSuccess] = useState(null);
  const [verifyingMeter, setVerifyingMeter] = useState(false);
  const [verifiedCustomer, setVerifiedCustomer] = useState(null);
  const [verifyingSmartcard, setVerifyingSmartcard] = useState(false);
  const [verifiedSubscriber, setVerifiedSubscriber] = useState(null);
  const [utilityForm] = Form.useForm();

  useEffect(() => {
    fetchClaimDetails();
  }, [claim_token]);

  const fetchClaimDetails = async () => {
    setLoading(true);
    setError(null);
    let receiptErrorMsg = null;

    // 1. Check if token corresponds to a Receipt Loyalty Claim
    try {
      const res = await axios.get(
        `${API_BASE}/api/v2/loyalty/claims/${claim_token}`
      );
      if (res.data && res.data.success && res.data.sale) {
        setClaimType("loyalty_receipt");
        setReceiptData(res.data);
        setLoading(false);
        return;
      }
    } catch (err) {
      receiptErrorMsg =
        err.response?.data?.message || err.response?.data?.error;
    }

    // 2. Check if token corresponds to a Utility Reward Claim
    try {
      const res = await axios.get(
        `${API_BASE}/api/v1/utility_rewards/claims/${claim_token}`
      );
      if (res.data && res.data.success) {
        setClaimType("utility_reward");
        setClaimData(res.data.claim);
        if (res.data.catalog) {
          setCatalog(res.data.catalog);
        }
        if (res.data.claim.status === "claimed") {
          setClaimSuccess({
            voucher_code: res.data.claim.fulfilled_order?.voucher_code,
            service_type: res.data.claim.fulfilled_order?.service_type,
            beneficiary_number:
              res.data.claim.fulfilled_order?.beneficiary_number,
            already_claimed: true,
          });
        }
        setLoading(false);
        return;
      } else {
        setError(res.data?.error || "Failed to load reward claim details.");
      }
    } catch (err) {
      setError(
        receiptErrorMsg ||
          err.response?.data?.error ||
          "Could not find a valid reward or receipt for this link."
      );
    } finally {
      setLoading(false);
    }
  };

  // --- Loyalty Claim Handler ---
  const handleLoyaltyClaim = async (values) => {
    if (!loyaltyPhone || loyaltyPhone.trim().length < 8) {
      message.error("Please enter a valid phone number to claim loyalty points.");
      return;
    }

    setClaimingLoyalty(true);
    try {
      const formattedPhone = loyaltyPhone.startsWith("+")
        ? loyaltyPhone
        : `+${loyaltyPhone}`;

      const payload = {
        sale_id: receiptData?.sale?.id || claim_token,
        phone_number: formattedPhone,
        first_name: values?.first_name || "",
        last_name: values?.last_name || "",
      };

      const res = await axios.post(
        `${API_BASE}/api/v2/loyalty/claim_points`,
        payload
      );

      if (res.data && res.data.success) {
        setLoyaltyClaimSuccess(res.data);
        if (res.data.status === "pending" || res.data.pending) {
          message.success("Loyalty claim submitted! Awaiting store approval. ⏳");
        } else {
          message.success("Loyalty points claimed successfully! 🎉");
        }

        try {
          if (res.data.customer) {
            localStorage.setItem(
              "user_customer_details",
              JSON.stringify(res.data.customer)
            );
          }
        } catch (e) {
          // localStorage error handling
        }
      } else {
        message.error(res.data?.message || "Failed to claim points.");
      }
    } catch (err) {
      const errMsg =
        err.response?.data?.message ||
        err.response?.data?.error ||
        "An error occurred while claiming your loyalty points. Please try again.";
      message.error(errMsg);

      if (err.response?.data?.is_claimed) {
        setReceiptData((prev) => ({
          ...prev,
          sale: { ...prev.sale, is_claimed: true },
        }));
      }
      if (err.response?.data?.is_expired) {
        setReceiptData((prev) => ({
          ...prev,
          sale: { ...prev.sale, is_expired: true },
        }));
      }
    } finally {
      setClaimingLoyalty(false);
    }
  };

  // --- Utility Reward Verifications ---
  const handleVerifyMeter = async () => {
    const values = utilityForm.getFieldsValue();
    if (!values.meter || !values.electricity_biller) {
      message.error("Please select an electricity provider and enter meter number.");
      return;
    }
    setVerifyingMeter(true);
    setVerifiedCustomer(null);
    try {
      const res = await axios.post(
        `${API_BASE}/api/v1/utility_rewards/claims/${claim_token}/verify_meter`,
        {
          meter: values.meter,
          biller: values.electricity_biller,
          product: values.meter_type || "Prepaid",
        }
      );
      if (res.data && res.data.success) {
        setVerifiedCustomer(res.data.data);
        message.success(
          `Verified: ${res.data.data.name || res.data.data.beneficiary_name || "Meter Valid"}`
        );
      } else {
        message.error(res.data?.error || "Meter verification failed.");
      }
    } catch (err) {
      message.error(err.response?.data?.error || "Meter verification failed.");
    } finally {
      setVerifyingMeter(false);
    }
  };

  const handleVerifySmartcard = async () => {
    const values = utilityForm.getFieldsValue();
    if (!values.smartcard_number || !values.cable_biller) {
      message.error("Please select a cable provider and enter smartcard/IUC number.");
      return;
    }
    setVerifyingSmartcard(true);
    setVerifiedSubscriber(null);
    try {
      const res = await axios.post(
        `${API_BASE}/api/v1/utility_rewards/claims/${claim_token}/verify_smartcard`,
        {
          number: values.smartcard_number,
          biller: values.cable_biller,
          product: values.cable_product || values.cable_biller || "",
        }
      );
      if (res.data && res.data.success) {
        setVerifiedSubscriber(res.data.data);
        message.success(
          `Verified: ${res.data.data.name || res.data.data.subscriber_name || "Smartcard Valid"}`
        );
      } else {
        message.error(res.data?.error || "Smartcard verification failed.");
      }
    } catch (err) {
      message.error(err.response?.data?.error || "Smartcard verification failed.");
    } finally {
      setVerifyingSmartcard(false);
    }
  };

  const handleUtilityClaimSubmit = async (values) => {
    setSubmitting(true);
    try {
      let payload = { service_type: activeTab };

      if (activeTab === "airtime") {
        payload.network = values.airtime_network;
        payload.phone = values.airtime_phone;
      } else if (activeTab === "data") {
        payload.network = values.data_network;
        payload.phone = values.data_phone;
        payload.bundle_code = values.data_bundle;
      } else if (activeTab === "electricity") {
        payload.meter = values.meter;
        payload.biller = values.electricity_biller;
        payload.product = values.meter_type || "Prepaid";
        payload.name = verifiedCustomer?.name || verifiedCustomer?.beneficiary_name;
        payload.address = verifiedCustomer?.address;
      } else if (activeTab === "cable") {
        payload.biller = values.cable_biller;
        payload.number = values.smartcard_number;
        payload.product = values.cable_product || values.cable_biller || "";
        payload.name = verifiedSubscriber?.name || verifiedSubscriber?.subscriber_name;
      }

      const res = await axios.post(
        `${API_BASE}/api/v1/utility_rewards/claims/${claim_token}/redeem`,
        payload
      );

      if (res.data && res.data.success) {
        setClaimSuccess({
          voucher_code: res.data.voucher_code,
          service_type: activeTab,
          beneficiary_number:
            payload.meter || payload.phone || payload.number,
          message: res.data.message,
        });
        message.success("Reward claimed successfully!");
      } else {
        message.error(res.data?.error || "Failed to claim reward.");
      }
    } catch (err) {
      message.error(
        err.response?.data?.error ||
          "An error occurred while claiming your reward. Please check details."
      );
    } finally {
      setSubmitting(false);
    }
  };

  const copyToClipboard = (text) => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(text);
      message.success("Copied to clipboard!");
    } else {
      message.info("Voucher: " + text);
    }
  };

  if (loading) {
    return (
      <div className="claim-reward-loading">
        <Spin size="large" />
        <p style={{ marginTop: 16, color: "#6B6B76", fontSize: 14 }}>
          Loading order details...
        </p>
      </div>
    );
  }

  if (error || (!receiptData && !claimData)) {
    return (
      <div className="claim-reward-container">
        <Card className="claim-card-error">
          <ClockCircleOutlined className="error-icon" />
          <Title level={3} style={{ marginTop: 16, marginBottom: 8, fontSize: 20 }}>
            Claim Link Unavailable
          </Title>
          <Paragraph type="secondary" style={{ fontSize: 14, marginBottom: 24 }}>
            {error || "This order claim link is invalid or expired."}
          </Paragraph>
          <Button
            type="primary"
            className="shopify-primary-btn"
            onClick={() => history.push("/home")}
          >
            Go to Loystar Shop
          </Button>
        </Card>
      </div>
    );
  }

  // =========================================================================
  // VIEW 1: MINIMAL SHOPIFY-STYLE RECEIPT LOYALTY CLAIM
  // =========================================================================
  if (claimType === "loyalty_receipt" && receiptData) {
    const sale = receiptData.sale || {};
    const merchant = receiptData.merchant || {};
    const loyalty = receiptData.loyalty || {};
    const isStamps = loyalty.program_type === "StampsProgram";
    const awardAmount = loyalty.points_to_award || 1;
    const isAlreadyClaimed = sale.is_claimed;
    const isPending = sale.is_pending;
    const currency = sale.currency_symbol || sale.currency || "₦";

    const defaultCountry = (() => {
      const c = (merchant.country || "").toLowerCase().trim();
      if (c === "nigeria" || c === "ng") return "ng";
      if (c === "ghana" || c === "gh") return "gh";
      if (c === "kenya" || c === "ke") return "ke";
      if (c === "united states" || c === "us" || c === "usa") return "us";
      if (c === "united kingdom" || c === "gb" || c === "uk") return "gb";
      if (c === "south africa" || c === "za") return "za";
      if (c === "uganda" || c === "ug") return "ug";
      if (c === "rwanda" || c === "rw") return "rw";
      if (c === "canada" || c === "ca") return "ca";
      const cur = (sale.currency || "").toUpperCase();
      if (cur === "GHS") return "gh";
      if (cur === "KES") return "ke";
      if (cur === "USD") return "us";
      if (cur === "GBP") return "gb";
      if (cur === "ZAR") return "za";
      if (cur === "UGX") return "ug";
      if (cur === "RWF") return "rw";
      if (cur === "CAD") return "ca";
      if (cur === "EUR") return "de";
      return "ng";
    })();

    return (
      <div className="claim-reward-page">
        {/* Minimal Store Branding */}
        <div className="claim-reward-header">
          {merchant.logo_url ? (
            <img
              src={merchant.logo_url}
              alt={merchant.business_name}
              className="merchant-logo"
            />
          ) : (
            <div className="merchant-logo-placeholder">
              {merchant.business_name?.charAt(0)?.toUpperCase() || "S"}
            </div>
          )}
          <Title level={4} className="merchant-title">
            {merchant.business_name || "Store"}
          </Title>
          {merchant.address && (
            <div className="merchant-address">{merchant.address}</div>
          )}
          <div>
            <span className="verified-pill">
              <SafetyCertificateOutlined />
              Verified Loystar Merchant
            </span>
          </div>
        </div>

        {/* Shopify-Style Collapsible Order Summary */}
        <div className="shopify-order-summary-box">
          <div
            className="shopify-order-summary-toggle"
            onClick={() => setSummaryOpen(!summaryOpen)}
          >
            <div className="shopify-order-summary-left">
              <ShoppingOutlined />
              <span>{summaryOpen ? "Hide order summary" : "Show order summary"}</span>
              {summaryOpen ? <UpOutlined style={{ fontSize: 10 }} /> : <DownOutlined style={{ fontSize: 10 }} />}
            </div>
            <div className="shopify-order-summary-total">
              {currency}{" "}
              {Number(sale.total || 0).toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </div>
          </div>

          {summaryOpen && (
            <div className="shopify-order-summary-content">
              <div className="shopify-order-meta">
                <span>Order #{sale.reference_code || sale.id}</span>
                <span>
                  {sale.created_at
                    ? moment(sale.created_at).format("MMM D, YYYY · h:mm A")
                    : "Recent Order"}
                </span>
              </div>

              {sale.items && sale.items.length > 0 ? (
                <div>
                  {sale.items.map((item, idx) => (
                    <div key={idx} className="shopify-item-row">
                      <div className="shopify-item-info">
                        <div className="shopify-item-thumb">
                          {item.name?.charAt(0)?.toUpperCase() || "•"}
                          {(item.quantity > 1 || item.quantity) && (
                            <span className="shopify-item-qty-badge">
                              {item.quantity || 1}
                            </span>
                          )}
                        </div>
                        <span className="shopify-item-name">{item.name}</span>
                      </div>
                      <span className="shopify-item-price">
                        {currency}{" "}
                        {Number(
                          item.amount || item.unit_price * (item.quantity || 1)
                        ).toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </span>
                    </div>
                  ))}
                  <Divider className="shopify-summary-divider" />
                </div>
              ) : null}

              <div className="shopify-summary-total-row">
                <span>Total</span>
                <span>
                  {currency}{" "}
                  {Number(sale.total || 0).toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Minimal Reward Announcement Card */}
        <div className="reward-callout-card">
          <div className="reward-callout-pill">
            <GiftOutlined />
            {isStamps ? "Loyalty Stamp" : "Loyalty Reward"}
          </div>

          <div className="reward-callout-amount">
            +{awardAmount} {isStamps ? (awardAmount === 1 ? "Stamp" : "Stamps") : "Points"}
          </div>

          <Paragraph className="reward-callout-sub">
            {isStamps ? (
              <>
                Earned on your visit to <strong>{merchant.business_name}</strong>.
                {loyalty.threshold && loyalty.reward && (
                  <span>
                    {" "}Collect {loyalty.threshold} stamps to redeem{" "}
                    <strong>{loyalty.reward}</strong>.
                  </span>
                )}
              </>
            ) : (
              <>
                Earned for your purchase at{" "}
                <strong>{merchant.business_name}</strong>.
                {loyalty.reward && (
                  <span>
                    {" "}Unlocks: <strong>{loyalty.reward}</strong>
                  </span>
                )}
              </>
            )}
          </Paragraph>
        </div>

        {/* STATES: Claim Form / Already Claimed / Success Confirmation */}
        {loyaltyClaimSuccess ? (
          /* SUCCESS STATE */
          <div className="claim-success-card">
            <div
              className="success-check-circle"
              style={
                loyaltyClaimSuccess.pending || loyaltyClaimSuccess.status === "pending"
                  ? { background: "#FEF3C7", color: "#D97706" }
                  : undefined
              }
            >
              {loyaltyClaimSuccess.pending || loyaltyClaimSuccess.status === "pending" ? (
                <ClockCircleOutlined />
              ) : (
                <CheckCircleOutlined />
              )}
            </div>
            <Title level={3} className="success-title">
              {loyaltyClaimSuccess.pending || loyaltyClaimSuccess.status === "pending"
                ? "Claim Submitted! ⏳"
                : "Points Claimed! 🎉"}
            </Title>
            <Paragraph className="success-subtitle">
              {loyaltyClaimSuccess.pending || loyaltyClaimSuccess.status === "pending" ? (
                <>
                  Your claim for{" "}
                  <strong>
                    {loyaltyClaimSuccess.customer?.phone_number ||
                      (loyaltyPhone.startsWith("+")
                        ? loyaltyPhone
                        : `+${loyaltyPhone}`)}
                  </strong>{" "}
                  is pending approval from{" "}
                  <strong>{merchant.business_name || "the store"}</strong>. Points will be credited once confirmed.
                </>
              ) : (
                <>
                  Credited to{" "}
                  <strong>
                    {loyaltyClaimSuccess.customer?.phone_number ||
                      (loyaltyPhone.startsWith("+")
                        ? loyaltyPhone
                        : `+${loyaltyPhone}`)}
                  </strong>
                </>
              )}
            </Paragraph>

            <div className="balance-card-minimal">
              <div>
                <div
                  className="balance-card-val"
                  style={{
                    color:
                      loyaltyClaimSuccess.pending || loyaltyClaimSuccess.status === "pending"
                        ? "#D97706"
                        : "#059669",
                  }}
                >
                  +{loyaltyClaimSuccess.points_awarded ||
                    loyaltyClaimSuccess.points ||
                    loyaltyClaimSuccess.stamps_awarded ||
                    awardAmount}
                </div>
                <div className="balance-card-label">
                  {loyaltyClaimSuccess.pending || loyaltyClaimSuccess.status === "pending"
                    ? "Pending Points"
                    : "Earned Today"}
                </div>
              </div>
              <Divider type="vertical" style={{ height: 40 }} />
              <div>
                <div className="balance-card-val">
                  {loyaltyClaimSuccess.pending || loyaltyClaimSuccess.status === "pending"
                    ? "Pending Admin"
                    : (loyaltyClaimSuccess.total_points ??
                       loyaltyClaimSuccess.total_stamps ??
                       awardAmount)}
                </div>
                <div className="balance-card-label">
                  {loyaltyClaimSuccess.pending || loyaltyClaimSuccess.status === "pending"
                    ? "Approval Status"
                    : `Total ${isStamps ? "Stamps" : "Points"}`}
                </div>
              </div>
            </div>

            {loyaltyClaimSuccess.card_linked && (
              <Alert
                type="info"
                message="Card linked for automatic rewards"
                description="Future card payments at this store will earn points automatically."
                showIcon
                style={{
                  borderRadius: 10,
                  marginBottom: 16,
                  textAlign: "left",
                  fontSize: 13,
                }}
              />
            )}

            <Button
              type="primary"
              size="large"
              block
              className="shopify-primary-btn"
              onClick={() => {
                const p =
                  loyaltyClaimSuccess.customer?.phone_number ||
                  (loyaltyPhone.startsWith("+")
                    ? loyaltyPhone
                    : `+${loyaltyPhone}`);
                history.push(`/my?${p.replace(/^\+/, "")}`);
              }}
            >
              View Loyalty Passbook
            </Button>

            <Button
              type="default"
              size="large"
              block
              className="shopify-secondary-btn"
              style={{ marginTop: 10 }}
              onClick={() => history.push("/home")}
            >
              Return to Shop
            </Button>
          </div>
        ) : isAlreadyClaimed ? (
          /* ALREADY CLAIMED STATE */
          <div className="claimed-notice-box">
            <div className="claimed-notice-icon">
              <InfoCircleOutlined />
            </div>
            <Title level={4} style={{ fontSize: 17, fontWeight: 600, marginBottom: 4 }}>
              Points Already Claimed
            </Title>
            <Paragraph style={{ color: "#6B6B76", fontSize: 13, marginBottom: 20 }}>
              {sale.claimed_by_mask
                ? `The loyalty points for this order were already claimed by ${sale.claimed_by_mask}.`
                : "The loyalty points for this order have already been claimed."}
            </Paragraph>
            <Button
              type="primary"
              size="large"
              block
              className="shopify-primary-btn"
              onClick={() => history.push("/my")}
            >
              View Loyalty Passbook
            </Button>
            <Button
              type="default"
              size="large"
              block
              className="shopify-secondary-btn"
              style={{ marginTop: 10 }}
              onClick={() => history.push("/home")}
            >
              Continue to Shop
            </Button>
          </div>
        ) : isPending ? (
          /* PENDING APPROVAL STATE */
          <div className="claimed-notice-box">
            <div className="claimed-notice-icon" style={{ background: "#FEF3C7", color: "#D97706" }}>
              <ClockCircleOutlined />
            </div>
            <Title level={4} style={{ fontSize: 17, fontWeight: 600, marginBottom: 4 }}>
              Claim Pending Store Approval
            </Title>
            <Paragraph style={{ color: "#6B6B76", fontSize: 13, marginBottom: 20 }}>
              {sale.claimed_by_mask
                ? `A loyalty claim for this receipt was submitted by ${sale.claimed_by_mask} and is awaiting admin approval before points are credited.`
                : "A loyalty claim for this receipt was submitted and is awaiting store approval."}
            </Paragraph>
            <Button
              type="primary"
              size="large"
              block
              className="shopify-primary-btn"
              onClick={() => history.push("/my")}
            >
              View Loyalty Passbook
            </Button>
            <Button
              type="default"
              size="large"
              block
              className="shopify-secondary-btn"
              style={{ marginTop: 10 }}
              onClick={() => history.push("/home")}
            >
              Continue to Shop
            </Button>
          </div>
        ) : sale.is_expired ? (
          /* EXPIRED STATE */
          <div className="claimed-notice-box">
            <div className="claimed-notice-icon" style={{ background: "#FEF2F2", color: "#DC2626" }}>
              <ClockCircleOutlined />
            </div>
            <Title level={4} style={{ fontSize: 17, fontWeight: 600, marginBottom: 4 }}>
              Claim Window Expired
            </Title>
            <Paragraph style={{ color: "#6B6B76", fontSize: 13, marginBottom: 20 }}>
              Loyalty points must be claimed within 48 hours of purchase. This receipt was issued on{" "}
              {sale.created_at ? moment(sale.created_at).format("MMM D, YYYY") : "an earlier date"}.
            </Paragraph>
            <Button
              type="primary"
              size="large"
              block
              className="shopify-primary-btn"
              onClick={() => history.push("/my")}
            >
              View Loyalty Passbook
            </Button>
            <Button
              type="default"
              size="large"
              block
              className="shopify-secondary-btn"
              style={{ marginTop: 10 }}
              onClick={() => history.push("/home")}
            >
              Continue to Shop
            </Button>
          </div>
        ) : (
          /* CLAIM FORM */
          <div className="claim-form-card">
            <Title level={4} className="claim-form-title">
              Collect Your Points
            </Title>
            <Paragraph className="claim-form-desc">
              Enter your mobile phone number. Points are saved directly to your account.
            </Paragraph>

            <Form
              form={loyaltyForm}
              layout="vertical"
              onFinish={handleLoyaltyClaim}
            >
              <Form.Item required style={{ marginBottom: 14 }}>
                <span className="shopify-input-label">Mobile Phone Number</span>
                <PhoneInput
                  country={defaultCountry}
                  value={loyaltyPhone}
                  onChange={(val) => setLoyaltyPhone(val)}
                  enableSearch
                  searchPlaceholder="Search country..."
                  containerStyle={{ width: "100%" }}
                  inputStyle={{
                    width: "100%",
                    height: "46px",
                    fontSize: "14px",
                    borderRadius: "10px",
                    borderColor: "#E8E8EE",
                    paddingLeft: "48px",
                  }}
                  buttonStyle={{
                    borderRadius: "10px 0 0 10px",
                    borderColor: "#E8E8EE",
                  }}
                />
              </Form.Item>

              <Row gutter={12}>
                <Col span={12}>
                  <Form.Item name="first_name" style={{ marginBottom: 14 }}>
                    <span className="shopify-input-label">First Name (Optional)</span>
                    <Input
                      placeholder="e.g. Jane"
                      style={{ borderRadius: 10, height: 44, borderColor: "#E8E8EE" }}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="last_name" style={{ marginBottom: 14 }}>
                    <span className="shopify-input-label">Last Name (Optional)</span>
                    <Input
                      placeholder="e.g. Doe"
                      style={{ borderRadius: 10, height: 44, borderColor: "#E8E8EE" }}
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Button
                type="primary"
                htmlType="submit"
                size="large"
                block
                loading={claimingLoyalty}
                className="shopify-primary-btn"
              >
                Claim +{awardAmount} {isStamps ? "Stamp" : "Points"}
              </Button>

              <div className="trust-microcopy">
                <LockOutlined />
                <span>Instant credit · No app download required</span>
              </div>
            </Form>
          </div>
        )}
      </div>
    );
  }

  // =========================================================================
  // VIEW 2: UTILITY REWARD CLAIM (Electricity, Airtime, Data, Cable)
  // =========================================================================
  const currencySymbol =
    claimData.currency_symbol ||
    (claimData.currency === "GBP"
      ? "£"
      : claimData.currency === "GHS"
      ? "GH₵"
      : claimData.currency === "USD"
      ? "$"
      : claimData.currency === "EUR"
      ? "€"
      : claimData.currency === "KES"
      ? "KSh"
      : claimData.currency === "ZAR"
      ? "R"
      : "₦");

  return (
    <div className="claim-reward-page">
      <div className="claim-reward-header">
        {claimData.merchant?.logo_url ? (
          <img
            src={claimData.merchant.logo_url}
            alt={claimData.merchant.business_name}
            className="merchant-logo"
          />
        ) : (
          <div className="merchant-logo-placeholder">
            {claimData.merchant?.business_name?.charAt(0)?.toUpperCase() || "L"}
          </div>
        )}
        <Title level={4} className="merchant-title">
          {claimData.merchant?.business_name}
        </Title>
        <div>
          <span className="verified-pill">
            <GiftOutlined />
            Utility Reward
          </span>
        </div>
      </div>

      <div>
        {claimSuccess ? (
          <div className="claim-success-card">
            <div className="success-check-circle">
              <CheckCircleOutlined />
            </div>
            <Title level={3} className="success-title">
              {claimSuccess.already_claimed
                ? "Reward Already Claimed"
                : "Reward Claimed! 🎉"}
            </Title>
            <Paragraph className="success-subtitle">
              Your{" "}
              <strong>
                {currencySymbol}
                {claimData.amount.toLocaleString()}
              </strong>{" "}
              reward has been generated.
            </Paragraph>

            {claimSuccess.voucher_code ? (
              <div
                style={{
                  background: "#F6F6F8",
                  border: "1px solid #E8E8EE",
                  borderRadius: 12,
                  padding: "16px 20px",
                  margin: "20px 0",
                }}
              >
                <Text
                  style={{
                    display: "block",
                    fontSize: 11,
                    fontWeight: 600,
                    color: "#6B6B76",
                    textTransform: "uppercase",
                    letterSpacing: 0.5,
                    marginBottom: 8,
                  }}
                >
                  Your Recharge Token
                </Text>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 12,
                    background: "#FFFFFF",
                    border: "1px solid #E8E8EE",
                    borderRadius: 8,
                    padding: "10px 14px",
                    marginBottom: 8,
                  }}
                >
                  <span
                    style={{
                      fontFamily: "monospace",
                      fontSize: 18,
                      fontWeight: 700,
                      color: "#0F0F10",
                      letterSpacing: 1,
                    }}
                  >
                    {claimSuccess.voucher_code}
                  </span>
                  <Button
                    type="default"
                    size="small"
                    icon={<CopyOutlined />}
                    onClick={() => copyToClipboard(claimSuccess.voucher_code)}
                  >
                    Copy
                  </Button>
                </div>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Enter this token into your meter or interface to recharge.
                </Text>
              </div>
            ) : (
              <Alert
                type="success"
                message="Sent Directly to Your Line"
                description={`The ${claimSuccess.service_type || "utility"} reward has been sent to ${claimSuccess.beneficiary_number || "your line"}.`}
                showIcon
                style={{ borderRadius: 10, textAlign: "left", marginBottom: 20 }}
              />
            )}

            <Button
              type="primary"
              size="large"
              block
              className="shopify-primary-btn"
              onClick={() => history.push("/home")}
            >
              Done / Return to Shop
            </Button>
          </div>
        ) : (
          <div className="claim-form-card">
            <div className="reward-hero-utility">
              <Text
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  color: "#6B6B76",
                  textTransform: "uppercase",
                  letterSpacing: 0.5,
                }}
              >
                Reward Value
              </Text>
              <div
                style={{
                  fontSize: 32,
                  fontWeight: 700,
                  color: "#0A0A0A",
                  margin: "4px 0",
                }}
              >
                {currencySymbol}
                {claimData.amount.toLocaleString()}
              </div>
              <Text type="secondary" style={{ fontSize: 13 }}>
                Credit from {claimData.merchant?.business_name}
              </Text>
            </div>

            <Tabs
              activeKey={activeTab}
              onChange={(key) => {
                setActiveTab(key);
                setVerifiedCustomer(null);
                setVerifiedSubscriber(null);
              }}
              className="claim-tabs"
            >
              <TabPane
                tab={
                  <span>
                    <ThunderboltOutlined /> Electricity
                  </span>
                }
                key="electricity"
              >
                <Form
                  form={utilityForm}
                  layout="vertical"
                  onFinish={handleUtilityClaimSubmit}
                  initialValues={{
                    meter_type: "Prepaid",
                    electricity_biller: "EKEDC_PREPAID",
                  }}
                >
                  <Form.Item
                    label="Electricity Provider"
                    name="electricity_biller"
                    rules={[{ required: true, message: "Select provider" }]}
                  >
                    <Select size="large" style={{ borderRadius: 10 }}>
                      {catalog.electricity_billers.map((b) => (
                        <Option key={b.code} value={b.code}>
                          {b.name}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>

                  <Form.Item label="Meter Type" name="meter_type">
                    <Select size="large" style={{ borderRadius: 10 }}>
                      <Option value="Prepaid">Prepaid</Option>
                      <Option value="Postpaid">Postpaid</Option>
                    </Select>
                  </Form.Item>

                  <Form.Item
                    label="Meter Number"
                    name="meter"
                    rules={[{ required: true, message: "Enter meter number" }]}
                  >
                    <Input
                      size="large"
                      placeholder="e.g. 0101150481949"
                      style={{ borderRadius: 10 }}
                      addonAfter={
                        <Button
                          type="link"
                          loading={verifyingMeter}
                          onClick={handleVerifyMeter}
                          style={{ height: 32, padding: 0 }}
                        >
                          Verify
                        </Button>
                      }
                    />
                  </Form.Item>

                  {verifiedCustomer && (
                    <Alert
                      type="success"
                      message={verifiedCustomer.name || verifiedCustomer.beneficiary_name}
                      description={verifiedCustomer.address || "Verified meter"}
                      showIcon
                      style={{ marginBottom: 16, borderRadius: 10 }}
                    />
                  )}

                  <Button
                    type="primary"
                    htmlType="submit"
                    size="large"
                    block
                    loading={submitting}
                    className="shopify-primary-btn"
                  >
                    Claim {currencySymbol}{claimData.amount.toLocaleString()} Token
                  </Button>
                </Form>
              </TabPane>

              <TabPane
                tab={
                  <span>
                    <PhoneOutlined /> Airtime
                  </span>
                }
                key="airtime"
              >
                <Form
                  form={utilityForm}
                  layout="vertical"
                  onFinish={handleUtilityClaimSubmit}
                  initialValues={{
                    airtime_network: "mtn",
                    airtime_phone: claimData.customer_phone || "",
                  }}
                >
                  <Form.Item
                    label="Mobile Network"
                    name="airtime_network"
                    rules={[{ required: true, message: "Select network" }]}
                  >
                    <Select size="large" style={{ borderRadius: 10 }}>
                      {catalog.airtime_networks.map((n) => (
                        <Option key={n.code} value={n.code}>
                          {n.name}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>

                  <Form.Item
                    label="Recipient Phone Number"
                    name="airtime_phone"
                    rules={[{ required: true, message: "Enter phone number" }]}
                  >
                    <Input size="large" placeholder="08012345678" style={{ borderRadius: 10 }} />
                  </Form.Item>

                  <Button
                    type="primary"
                    htmlType="submit"
                    size="large"
                    block
                    loading={submitting}
                    className="shopify-primary-btn"
                  >
                    Send Airtime
                  </Button>
                </Form>
              </TabPane>

              <TabPane
                tab={
                  <span>
                    <WifiOutlined /> Data
                  </span>
                }
                key="data"
              >
                <Form
                  form={utilityForm}
                  layout="vertical"
                  onFinish={handleUtilityClaimSubmit}
                  initialValues={{
                    data_network: "mtn",
                    data_phone: claimData.customer_phone || "",
                  }}
                >
                  <Form.Item
                    label="Mobile Network"
                    name="data_network"
                    rules={[{ required: true, message: "Select network" }]}
                  >
                    <Select size="large" style={{ borderRadius: 10 }}>
                      {catalog.airtime_networks.map((n) => (
                        <Option key={n.code} value={n.code}>
                          {n.name}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>

                  <Form.Item
                    label="Recipient Phone Number"
                    name="data_phone"
                    rules={[{ required: true, message: "Enter phone number" }]}
                  >
                    <Input size="large" placeholder="08012345678" style={{ borderRadius: 10 }} />
                  </Form.Item>

                  <Button
                    type="primary"
                    htmlType="submit"
                    size="large"
                    block
                    loading={submitting}
                    className="shopify-primary-btn"
                  >
                    Recharge Data
                  </Button>
                </Form>
              </TabPane>

              <TabPane
                tab={
                  <span>
                    <DesktopOutlined /> Cable TV
                  </span>
                }
                key="cable"
              >
                <Form
                  form={utilityForm}
                  layout="vertical"
                  onFinish={handleUtilityClaimSubmit}
                  initialValues={{ cable_biller: "gotv" }}
                >
                  <Form.Item
                    label="Cable Provider"
                    name="cable_biller"
                    rules={[{ required: true, message: "Select provider" }]}
                  >
                    <Select size="large" style={{ borderRadius: 10 }}>
                      {catalog.cable_billers.map((c) => (
                        <Option key={c.code} value={c.code}>
                          {c.name}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>

                  <Form.Item
                    label="Smartcard / IUC Number"
                    name="smartcard_number"
                    rules={[{ required: true, message: "Enter smartcard number" }]}
                  >
                    <Input
                      size="large"
                      placeholder="e.g. 1023456789"
                      style={{ borderRadius: 10 }}
                      addonAfter={
                        <Button
                          type="link"
                          loading={verifyingSmartcard}
                          onClick={handleVerifySmartcard}
                          style={{ height: 32, padding: 0 }}
                        >
                          Verify
                        </Button>
                      }
                    />
                  </Form.Item>

                  {verifiedSubscriber && (
                    <Alert
                      type="success"
                      message={verifiedSubscriber.name || verifiedSubscriber.subscriber_name}
                      showIcon
                      style={{ marginBottom: 16, borderRadius: 10 }}
                    />
                  )}

                  <Button
                    type="primary"
                    htmlType="submit"
                    size="large"
                    block
                    loading={submitting}
                    className="shopify-primary-btn"
                  >
                    Apply Cable Recharge
                  </Button>
                </Form>
              </TabPane>
            </Tabs>
          </div>
        )}
      </div>
    </div>
  );
};

export default ClaimRewardPage;
